/**
 * The column guide (DOP-627): every column of the tape, how it is made and
 * where it is read from, on one list.
 *
 * The Columns view of the Mapping tab. The Map and SQL views beside it show
 * the whole pipeline; this answers the analyst's question, "how is this
 * column made", a row at a time.
 */

import { Search } from "lucide-react";
import React, { useMemo, useState } from "react";
import type { LoanTapePreviewColumn } from "../Chat/Chat.types";
import {
	type ColumnOrigin,
	ORIGINS,
	ORIGIN_DOT,
	ORIGIN_LABEL,
	columnOrigin,
	countOrigins,
	sourceLabel,
} from "./loanTapeColumns";

type Filter = ColumnOrigin | "all";

export interface ColumnGuideProps {
	columns: readonly LoanTapePreviewColumn[];
	onOpenColumn: (column: LoanTapePreviewColumn) => void;
}

const CHIP =
	"inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-[13px] font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-[#008782]";

const howItsMade = (column: LoanTapePreviewColumn): string => {
	if (columnOrigin(column) === "unmapped") return "No source found in the new file.";
	return column.lineage?.transform || "Computed from the new file.";
};

const ColumnGuide: React.FC<ColumnGuideProps> = ({
	columns,
	onOpenColumn,
}) => {
	const [filter, setFilter] = useState<Filter>("all");
	const [query, setQuery] = useState("");
	const counts = useMemo(() => countOrigins(columns), [columns]);

	const needle = query.trim().toLowerCase();
	const shown = columns.filter(
		(column) =>
			(filter === "all" || columnOrigin(column) === filter) &&
			(needle === "" || column.header.toLowerCase().includes(needle)),
	);

	if (columns.length === 0) {
		return (
			<p className="rounded-xl border border-gray-200 bg-white px-4 py-6 text-center text-sm text-[#5B6472]">
				No columns yet. Run this workflow to build the tape.
			</p>
		);
	}

	return (
		<div className="flex flex-col gap-3.5">
			<div className="flex flex-wrap items-center gap-2">
				<button
					type="button"
					aria-pressed={filter === "all"}
					onClick={() => setFilter("all")}
					className={`${CHIP} ${
						filter === "all"
							? "border-[#004C4B] bg-[#004C4B] text-white"
							: "border-gray-200 bg-white text-[#3f4247] hover:bg-gray-50"
					}`}
				>
					All {columns.length}
				</button>
				{ORIGINS.map((origin) => (
					<button
						key={origin}
						type="button"
						aria-pressed={filter === origin}
						onClick={() => setFilter(origin)}
						className={`${CHIP} ${
							filter === origin
								? "border-[#004C4B] bg-[#004C4B] text-white"
								: "border-gray-200 bg-white text-[#3f4247] hover:bg-gray-50"
						}`}
					>
						<span
							aria-hidden
							className={`inline-block h-2 w-2 rounded-full ${ORIGIN_DOT[origin]}`}
						/>
						{ORIGIN_LABEL[origin]} · {counts[origin]}
					</button>
				))}
				<label className="ml-auto flex h-9 w-60 items-center gap-2 rounded-lg border border-[#d0d5dd] bg-white px-3 text-[13px] text-[#6B7280]">
					<Search className="h-[15px] w-[15px]" aria-hidden />
					<input
						value={query}
						onChange={(event) => setQuery(event.target.value)}
						placeholder="Find a column"
						aria-label="Find a column"
						className="min-w-0 flex-1 border-0 p-0 text-[13px] text-[#111111] outline-none focus:ring-0"
					/>
				</label>
			</div>

			<div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
				<div
					aria-hidden
					className="hidden items-center gap-4 border-b border-gray-200 bg-[#fafafa] px-[18px] py-2.5 text-xs font-semibold text-[#5B6472] md:flex"
				>
					<span className="w-56 flex-shrink-0">Your column</span>
					<span className="flex-1">How it's made</span>
					<span className="w-64 flex-shrink-0">In the new file</span>
				</div>
				{shown.length === 0 ? (
					<p className="px-[18px] py-6 text-center text-sm text-[#5B6472]">
						No column matches.
					</p>
				) : (
					<ul>
						{shown.map((column) => {
							const origin = columnOrigin(column);
							const sources = column.lineage?.sources ?? [];
							return (
								<li
									key={column.header}
									className="border-b border-[#f1f2f4] last:border-b-0"
								>
									<button
										type="button"
										onClick={() => onOpenColumn(column)}
										className={`flex w-full flex-col gap-1 px-[18px] py-[11px] text-left hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#008782] md:flex-row md:items-center md:gap-4 ${
											origin === "unmapped" ? "bg-[#fffdf7]" : ""
										}`}
									>
										<span className="flex w-56 flex-shrink-0 items-center gap-2 text-sm font-semibold text-[#111111]">
											<span
												aria-hidden
												className={`inline-block h-2 w-2 flex-shrink-0 rounded-full ${ORIGIN_DOT[origin]}`}
											/>
											{column.header}
										</span>
										<span
											className={`min-w-0 flex-1 text-[13px] ${
												origin === "unmapped"
													? "font-medium text-[#b54708]"
													: "text-[#3f4247]"
											}`}
										>
											{howItsMade(column)}
										</span>
										<span className="flex w-64 flex-shrink-0 flex-wrap gap-1">
											{sources.length === 0 ? (
												<span className="text-xs text-[#9a9ea3]">
													{origin === "unmapped" ? "not found" : "no source column"}
												</span>
											) : (
												sources.map((source) => (
													<span
														key={`${source.tab}:${source.column}:${source.canonical_field}`}
														className="inline-flex h-6 items-center rounded-md border border-[#eceae6] bg-[#f7f7f6] px-2 text-xs text-[#3f4247]"
													>
														{sourceLabel(source)}
													</span>
												))
											)}
										</span>
									</button>
								</li>
							);
						})}
					</ul>
				)}
			</div>

		</div>
	);
};

export default ColumnGuide;
